import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, CircularProgress, Button, TextField } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { queryBooksAPI } from '../../Api/index.ts';
import { listings } from '../Listings/data/listings';
import './ListingDetail.css';

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [bookCover, setBookCover] = useState('https://via.placeholder.com/300x400');
  const [loading, setLoading] = useState(true);
  const [coverLoading, setCoverLoading] = useState(true);
  const [offerAmount, setOfferAmount] = useState('');

  useEffect(() => {
    // Find the listing by ID
    const foundListing = listings.find(item => item.id === parseInt(id));
    if (foundListing) {
      setListing(foundListing);
      setOfferAmount(foundListing.price?.toString() || '');
      fetchBookCover(foundListing);
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchBookCover = async (listingData) => {
    if (!listingData.title && !listingData.isbn) {
      setCoverLoading(false);
      setLoading(false);
      return;
    }

    try {
      let query = listingData.isbn 
        ? `isbn:${listingData.isbn}`
        : `intitle:${listingData.title}${listingData.author ? ` inauthor:${listingData.author}` : ''}`;

      const response = await queryBooksAPI(query);
      const data = await response.json();

      if (!data.items?.length && listingData.title) {
        query = `${listingData.title} ${listingData.author || ''}`.trim();
        const fallbackResponse = await queryBooksAPI(query);
        const fallbackData = await fallbackResponse.json();
        if (fallbackData.items?.length) {
          data.items = fallbackData.items;
        }
      }

      if (data.items?.[0]?.volumeInfo?.imageLinks?.thumbnail) {
        const imageUrl = data.items[0].volumeInfo.imageLinks.thumbnail
          .replace('http://', 'https://')
          .replace('zoom=1', 'zoom=0');
        setBookCover(imageUrl);
      }
    } catch (error) {
      console.error('Error fetching book cover:', error);
    } finally {
      setCoverLoading(false);
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate('/listings');
  };

  const handleMakeOffer = () => {
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="listing-detail-container">
        <div className="listing-detail-content">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
          </div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="listing-detail-container">
        <div className="listing-detail-content">
          <Typography variant="h4" sx={{ textAlign: 'center', mt: 4 }}>
            Listing not found
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="listing-detail-container">
      <div className="listing-detail-content">
        <button 
          onClick={handleBackClick}
          className="back-button-listing"
        >
          <ArrowBackIcon style={{ marginRight: 6, fontSize: 20 }} />
          Back
        </button>

        <div className="listing-detail-card">
          <div className="listing-detail-image">
            {coverLoading ? (
              <div style={{ 
                width: '300px', 
                height: '400px', 
                backgroundColor: '#f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px'
              }}>
                <CircularProgress />
              </div>
            ) : (
              <img 
                src={bookCover} 
                alt="Book cover"
                style={{ 
                  width: '300px', 
                  height: '400px', 
                  objectFit: 'cover',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }} 
              />
            )}
          </div>

          <div className="listing-detail-info">
            <div className="listing-detail-header">
              <Typography variant="h4" component="h1" className="title">
                {listing.title}
              </Typography>
              <Typography variant="h6" className="author">
                by {listing.author}
              </Typography>
              <Typography variant="h5" className="price">
                ${listing.price}
              </Typography>
            </div>

            <div className="listing-detail-details">
              <div className="detail-row">
                <Typography variant="body1" className="detail-label">Course Code:</Typography>
                <Typography variant="body1" className="detail-value">{listing.courseCode}</Typography>
              </div>
              <div className="detail-row">
                <Typography variant="body1" className="detail-label">Condition:</Typography>
                <Typography variant="body1" className="detail-value">{listing.condition}</Typography>
              </div>
              <div className="detail-row">
                <Typography variant="body1" className="detail-label">Format:</Typography>
                <Typography variant="body1" className="detail-value">{listing.format}</Typography>
              </div>
              {listing.isbn && (
                <div className="detail-row">
                  <Typography variant="body1" className="detail-label">ISBN:</Typography>
                  <Typography variant="body1" className="detail-value">{listing.isbn}</Typography>
                </div>
              )}
            </div>

            <div className="offer-section">
              <Typography variant="h6" sx={{ mb: 2 }}>Make an Offer</Typography>
              <TextField
                type="number"
                label="Offer Amount ($)"
                value={offerAmount}
                onChange={(e) => setOfferAmount(e.target.value)}
                className="offer-input"
                sx={{ mb: 2 }}
              />
              <Button 
                variant="contained" 
                onClick={handleMakeOffer}
                className="make-offer-btn make-offer-btn-listing"
                style={{ background: '#6B89FF', color: 'white' }}
              >
                Make offer
              </Button>
            </div>

            <div className="seller-photos-section">
              <Typography variant="h6" sx={{ mb: 2 }}>Seller Photos</Typography>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {[1, 2, 3].map((photo) => (
                  <div 
                    key={photo}
                    style={{
                      width: '80px',
                      height: '80px',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      color: '#666'
                    }}
                  >
                    Photo {photo}
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
} 